// @ts-check
'use strict';

/**
 * The site condition report (Region Group's format, and the Higgins standard's
 * body): a card per area with its rating, ticks, notes, action and feature
 * photo; the pins' numbers and colours; a card per substrate across the site;
 * the maintenance outlook (each rated area's delivery mode and year); the key
 * risks; the recommendation. measure-to-quote's iPad screens use the colours and pin
 * numbers; its API fills the Word report from `buildSiteReport`.
 * Design: apps/measure-to-quote/docs/CONDITION-REGION.md "The report". Pure.
 */

const { BAND_RANK, bandOf, ratingLabel, worseBand } = require('./scales');
const { generateConditionNote } = require('./phrases');
const { aggregateKeyRisks } = require('./report');
const { BAND_DEFAULT_DELIVERY, BAND_RECOMMENDATION, DELIVERY_MODE_BY_KEY } = require('./vocabulary');

/** @typedef {import('./scales').Band} Band */

/** Each area's colour, by its place in the job's area list: as in Region Group's example reports. */
const AREA_COLOURS = Object.freeze(['#1f3f8f', '#5aa0e0', '#ff8300', '#e46aa7', '#4f8a3a', '#e3b505', '#8b189b', '#7d3a33', '#2a9d8f', '#55595e']);

/** @param {number} index the area's place in the job's area list, from 0 */
const areaColour = (index) => AREA_COLOURS[((index % AREA_COLOURS.length) + AREA_COLOURS.length) % AREA_COLOURS.length];

const COMPASS_POINTS = Object.freeze(['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']);

/** A compass bearing (0 = north, clockwise) to the nearest of eight points. @param {number} bearing */
const compassPoint = (bearing) => COMPASS_POINTS[Math.round((((bearing % 360) + 360) % 360) / (360 / COMPASS_POINTS.length)) % COMPASS_POINTS.length];

/**
 * @typedef {object} PinPhoto
 * @property {string} id
 * @property {string} areaId       the job's area (hig_measurearea) the photo is of
 * @property {string | null} [takenOn]
 */

/**
 * The site plan's pins: every External area's photo, numbered in area order
 * then the order taken, coloured by its area. The plan and the report both
 * number them this way, so a card's numbers are its pins'.
 *
 * @param {{ id: string, side?: string | null }[]} areas  the job's areas, in app order
 * @param {PinPhoto[]} photos                           the condition report's photos
 * @returns {{ id: string, areaId: string, number: number, colour: string }[]}
 */
function numberPins(areas, photos) {
  const indexOf = new Map(areas.map((a, i) => [a.id, i]));
  return photos
    .map((p) => ({ p, index: indexOf.get(p.areaId) ?? -1 }))
    .filter(({ index }) => index >= 0 && areas[index].side === 'External')
    .sort((a, b) => a.index - b.index || String(a.p.takenOn ?? '').localeCompare(String(b.p.takenOn ?? '')))
    .map(({ p, index }, i) => ({ id: p.id, areaId: p.areaId, number: i + 1, colour: areaColour(index) }));
}

/**
 * @typedef {object} SiteSurface
 * @property {string} name            the surface as the area names it ("Front fascia")
 * @property {string} substrate       its surface type ("Fascia"): the substrate card it joins
 * @property {string | null} [baseSurface]
 * @property {string | number | null} [rating]
 * @property {string | null} [note]
 * @property {string[]} [defects]
 * @property {string[]} [customDefects]
 * @property {string | null} [extent]
 * @property {string | null} [surfaceId]  the surface's id in the area, which its photos carry
 */

/**
 * @typedef {object} SitePhoto
 * @property {string} id
 * @property {string} fileName        "Walls 01.jpg"
 * @property {boolean} [feature]      the area's feature photo (#437)
 * @property {number | null} [bearing]
 * @property {string | null} [takenOn]
 * @property {string | null} [surfaceId]
 * @property {string | null} [webUrl] the file on SharePoint
 * @property {string | null} [caption] typed on the iPad; else the file name is the caption
 */

/**
 * @typedef {object} SiteArea
 * @property {string} id              the job's area
 * @property {string} name
 * @property {'Internal' | 'External' | null} side
 * @property {string | null} [action] Urgent · Do soon · Do later · N/A, as the measurer chose it
 * @property {string | null} [deliveryMode] a DELIVERY_MODES label ("Wash-Down Year"), as the measurer chose it
 * @property {number | null} [outlookYear]   the year the delivery mode falls due
 * @property {string | null} [building]      the building it is in ("Block A")
 * @property {string | null} [buildingCondition] that building's condition as last validated (Good · Fair · Poor)
 * @property {string[]} [ticks]       the condition ticks on (their keys)
 * @property {SiteSurface[]} surfaces rated or not
 * @property {SitePhoto[]} photos
 */

/**
 * @typedef {object} SiteReportInput
 * @property {string} scale                     a SCALES key
 * @property {'External' | 'Internal' | 'Both'} sides
 * @property {SiteArea[]} areas                 the job's areas, in app order (all of them: colours follow the whole list)
 * @property {{ key: string, label: string, side?: string | null }[]} ticks  the condition ticks set up in CRM
 * @property {Record<string, string>} [substrateNotes]  notes edited at the desk, by substrate
 * @property {string | null} [recommendation]   typed at the desk; else written from the ratings
 * @property {string | null} [siteSummary]
 * @property {string | null} [keyRisks]          typed at the desk; else the most frequent defects tapped
 */

/** The photo's caption, else its file name, with the way it faces. @param {SitePhoto} photo */
const photoCaption = (photo) => {
  const name = (photo.caption && photo.caption.trim()) || photo.fileName.replace(/\.jpe?g$/i, '');
  return typeof photo.bearing === 'number' ? `${name} · facing ${compassPoint(photo.bearing)}` : name;
};

/** The SharePoint folder a file is in, from the file's web address. @param {string | null | undefined} webUrl */
const folderOf = (webUrl) => (webUrl && webUrl.includes('/') ? webUrl.slice(0, webUrl.lastIndexOf('/')) : null);

/** @param {SitePhoto[]} photos */
const byTaken = (photos) => [...photos].sort((a, b) => String(a.takenOn ?? '').localeCompare(String(b.takenOn ?? '')));

/** The card's photo: the feature photo, else the first taken. @param {SitePhoto[]} photos */
function mainPhoto(photos) {
  const sorted = byTaken(photos);
  return sorted.find((p) => p.feature) ?? sorted[0] ?? null;
}

/**
 * The worst rated surface of a list, with its value; null when none is rated.
 * @param {SiteSurface[]} surfaces
 * @param {string} scale
 */
function worstRated(surfaces, scale) {
  /** @type {{ band: Band, rating: string | number } | null} */
  let worst = null;
  for (const s of surfaces) {
    const band = bandOf(scale, s.rating);
    if (!band || s.rating === null || s.rating === undefined) continue;
    if (!worst || BAND_RANK[band] > BAND_RANK[worst.band]) worst = { band, rating: s.rating };
  }
  return worst;
}

/**
 * The area's delivery mode: the measurer's choice, else the band's default; blank when neither.
 * @param {SiteArea} area
 * @param {Band | null} band
 */
function deliveryModeOf(area, band) {
  if (area.deliveryMode) return area.deliveryMode;
  return band ? DELIVERY_MODE_BY_KEY[BAND_DEFAULT_DELIVERY[band]].label : '';
}

/**
 * One area's card.
 * @param {SiteArea} area
 * @param {number} index        its place in the job's area list
 * @param {SiteReportInput} input
 * @param {number[]} pinNumbers its photos' pins
 */
function areaCard(area, index, input, pinNumbers) {
  const worst = worstRated(area.surfaces, input.scale);
  const offered = input.ticks.filter((t) => !t.side || t.side === 'Both' || t.side === area.side);
  const photo = mainPhoto(area.photos);
  const notes = area.surfaces.filter((s) => s.note && s.note.trim()).map((s) => `${s.name}: ${String(s.note).trim()}`);
  return {
    areaId: area.id,
    name: area.name,
    side: area.side,
    number: index + 1,
    colour: areaColour(index),
    pinNumbers,
    band: worst ? worst.band : null,
    rating: worst ? worst.rating : null,
    ratingLabel: worst ? ratingLabel(input.scale, worst.rating) : 'Not rated',
    ticks: offered.map((t) => ({ label: t.label, yes: (area.ticks ?? []).includes(t.key) })),
    notes: notes.join('\n'),
    action: area.action ?? '',
    deliveryMode: deliveryModeOf(area, worst ? worst.band : null),
    outlookYear: area.outlookYear ?? null,
    building: area.building ?? '',
    buildingCondition: area.buildingCondition ?? '',
    photo: photo ? { id: photo.id, caption: photoCaption(photo) } : null,
    photosFolderUrl: folderOf(photo ? photo.webUrl : null),
  };
}

/** @typedef {ReturnType<typeof areaCard>} AreaCard */

/** @param {string} side @param {SiteReportInput['sides']} sides */
const sideIncluded = (side, sides) => sides === 'Both' || side === sides;

/**
 * A card per substrate (surface type) across the reported areas: the worst
 * rating, the note (the desk's edit, else written from the ratings and
 * defects), and a photo of a surface of that type.
 * @param {SiteArea[]} areas
 * @param {SiteReportInput} input
 */
function substrateCards(areas, input) {
  /** @type {Map<string, { surfaces: SiteSurface[], photos: SitePhoto[] }>} */
  const bySubstrate = new Map();
  for (const area of areas) {
    for (const surface of area.surfaces) {
      if (!bandOf(input.scale, surface.rating)) continue;
      const entry = bySubstrate.get(surface.substrate) ?? { surfaces: [], photos: [] };
      entry.surfaces.push(surface);
      entry.photos.push(...area.photos.filter((p) => p.surfaceId && p.surfaceId === surface.surfaceId));
      bySubstrate.set(surface.substrate, entry);
    }
  }
  return [...bySubstrate].map(([substrate, { surfaces, photos }]) => {
    const worst = /** @type {{ band: Band, rating: string | number }} */ (worstRated(surfaces, input.scale));
    const defects = [...new Set(surfaces.flatMap((s) => s.defects ?? []))];
    const customDefects = [...new Set(surfaces.flatMap((s) => s.customDefects ?? []))];
    const edited = input.substrateNotes?.[substrate];
    const photo = byTaken(photos)[0] ?? null;
    return {
      substrate,
      band: worst.band,
      rating: worst.rating,
      ratingLabel: ratingLabel(input.scale, worst.rating),
      note: edited && edited.trim() ? edited.trim() : generateConditionNote(substrate, { band: worst.band, defects, customDefects }),
      noteEdited: Boolean(edited && edited.trim()),
      photo: photo ? { id: photo.id, caption: photoCaption(photo) } : null,
    };
  });
}

/**
 * The recommendation as typed at the desk, else one written from the areas'
 * ratings: per band, worst first, its areas and what to do ("Prioritise Front
 * retail: Address as a first priority …").
 * @param {AreaCard[]} cards
 * @param {string | null | undefined} typed
 */
function recommendationFor(cards, typed) {
  if (typed && typed.trim()) return typed.trim();
  /** @type {Band[]} */
  const bands = ['poor', 'fair', 'good'];
  return bands
    .map((band) => ({ band, areas: cards.filter((c) => c.band === band).map((c) => c.name) }))
    .filter((t) => t.areas.length)
    .map((t) => `${BAND_RECOMMENDATION[t.band].verb} ${t.areas.join(', ')}: ${BAND_RECOMMENDATION[t.band].lead}`)
    .join('\n');
}

/**
 * The key risks as typed at the desk, else the defects tapped most often
 * across the reported areas, numbered ("1. Peeling / flaking").
 * @param {SiteArea[]} areas
 * @param {string | null | undefined} typed
 */
function keyRisksFor(areas, typed) {
  if (typed && typed.trim()) return typed.trim();
  const items = areas.map((a) => ({ items: a.surfaces.map((s) => ({ defects: s.defects ?? [] })) }));
  return aggregateKeyRisks(/** @type {any} */ (items)).map((r, i) => `${i + 1}. ${r.label}`).join('\n');
}

/**
 * Everything the site condition report shows, from the visit's areas.
 * @param {SiteReportInput} input
 */
function buildSiteReport(input) {
  const pins = numberPins(input.areas, input.areas.flatMap((a) => a.photos.map((p) => ({ id: p.id, areaId: a.id, takenOn: p.takenOn }))));
  const cards = input.areas.map((area, index) =>
    areaCard(area, index, input, pins.filter((p) => p.areaId === area.id).map((p) => p.number)),
  );
  const reported = cards.filter((c) => c.side && sideIncluded(c.side, input.sides));
  const reportedAreas = input.areas.filter((a) => a.side && sideIncluded(a.side, input.sides));
  return {
    scale: input.scale,
    sides: input.sides,
    externalCards: reported.filter((c) => c.side === 'External'),
    internalCards: reported.filter((c) => c.side === 'Internal'),
    substrates: substrateCards(reportedAreas, input),
    // The maintenance outlook: every reported area that is rated, in the job's order.
    outlook: reported.filter((c) => c.band),
    recommendation: recommendationFor(reported, input.recommendation),
    keyRisks: keyRisksFor(reportedAreas, input.keyRisks),
    siteSummary: (input.siteSummary ?? '').trim(),
    overallBand: reported.reduce(/** @param {Band | null} w @param {AreaCard} c */ (w, c) => worseBand(w, c.band), null),
  };
}

/** @typedef {ReturnType<typeof buildSiteReport>} SiteReport */

module.exports = { AREA_COLOURS, areaColour, compassPoint, numberPins, buildSiteReport, photoCaption };
